// External
import { Injectable, SecurityContext } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DeviceDetectorService } from 'ngx-device-detector';

// Internal
import { OperatingSystems, PasswordManagementBrowsers, ValuesService } from '../../../values/values.service';
import { ConfigService } from '../../../config/config.service';
import { DeviceInfoService } from '../device-info/device-info.service';
import userAgentParser from 'ua-parser-js';

export type CustomValidatorInterface = {
    minlength?: boolean;
    maxlength?: boolean;
    pattern?: boolean;
    required?: boolean;
    ineligible?: boolean;
}

export type CustomValidatorErrorInterface = {
    [key in keyof CustomValidatorInterface]: string;
}

@Injectable({
    providedIn: 'root'
})
export class UsefulService {

    invokeEvent: Subject<any> = new Subject();
    invokeEventNotifications: Subject<any> = new Subject();
    invokeEventNotificationsCount: Subject<any> = new Subject();

    constructor(
        private readonly router: Router,
        public readonly _sanitizer: DomSanitizer,
        private readonly valuesService: ValuesService,
        private readonly translate: TranslateService,
        private readonly deviceDetectorService: DeviceDetectorService,
        private readonly configService: ConfigService,
        private readonly deviceInfoService: DeviceInfoService
    ) { }

    /**
     * Function that subtract no of years from today and adds one day
     * @param {number} years - number of years to subtract
     * @returns {Date}
     */
    public subtractYearsFromNow(years: number): Date {
        const now = new Date();
        const date = new Date(now.getFullYear() - years, now.getMonth(), now.getDate() + 1);

        return date;
    }

    isTabletOrMobileDevice() {
        return this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
    }

    getThisDeviceOSForRequest() {
        let deviceOS = this.deviceDetectorService.os.toLocaleLowerCase();
        return this.valuesService.compatibleOsInfo[deviceOS] ? this.valuesService.compatibleOsInfo[deviceOS].request : null;
    }

    getThisDeviceOSForDisplay() {
        let deviceOS = this.deviceDetectorService.os.toLocaleLowerCase();
        return this.valuesService.compatibleOsInfo[deviceOS] ? this.valuesService.compatibleOsInfo[deviceOS].display : null;
    }

    /**
     * Gets the os for display inside de install modal
     * @param {OperatingSystems} gievn os
     * @returns {string} Returns the os for display
     */
    getDeviceOSForDisplay(os: OperatingSystems): string {
        return this.valuesService.compatibleOsInfo[os]?.display ?? '';
    }

    getThisDeviceOSForDisplayAll() {
        const deviceOS = this.deviceDetectorService.os.toLocaleLowerCase();
        return this.valuesService.allOsDisplayInfo[deviceOS] ? this.valuesService.allOsDisplayInfo[deviceOS].display : null;
    }

    /**
    * This converts a JSON to query params string for URL
    * @param {object} json - Object to be converted {param1: <val1>, param2: <val2>}
    * @returns {string} Returns query string param1=<val1>&param2=<val2>
    */
    jsonToQueryString(json) {
        let params = new URLSearchParams();
        for(let key in json){
            params.set(key, json[key]);
        }
        return params.toString();
    }

    /**
    * This converts URL query params to JSON object
    * @param {string} queryString - String to be converted param1=<val1>&param2=<val2>
    * @returns {object} Returns object {param1: <val1>, param2: <val2>}
    */
    queryStringToJson(queryString): object{
        let json = {};
        if (queryString) {
            let groupedParams = queryString.split('&');
            for (let i in groupedParams) {
                let id = groupedParams[i].split('=')[0];
                let value = groupedParams[i].split('=')[1];
                json[id] = value;
            }
        }
        return json;
    }

    goToHowToInstallFaq() {
        this.router.navigate([this.valuesService.centralPaths.support.path],
                            {
                                queryParams: {
                                    [this.valuesService.openSupportFaqQueryParam]: this.valuesService.howToInstallSupportFaqIndex
                                }
                            });
    }

    objLength(obj) {
        return Object.keys(obj).length;
    }

    checkAndSetFirstLoginTimestamp() {
        try {
            const loginTimestamp = localStorage.getItem(this.valuesService.loginTimestamp);
            if (!loginTimestamp) {
                const timestamp = new Date().getTime();
                localStorage.setItem(this.valuesService.loginTimestamp, timestamp.toString());
            }
        } catch {}
    }

    setFirstLoginTimestamp() {
        const timestamp = new Date().getTime();
        localStorage.setItem(this.valuesService.loginTimestamp, timestamp.toString());
    }

    decodeHtmlEntity(str) {
        return str.replace(/&#(\d+);/g, function(_, dec) {
          return String.fromCharCode(dec);
        });
    }

    sanitizeValues (unsafe) {
        if(!unsafe?.replace){
            return '';
        }

        if(typeof unsafe !== 'string'){
            unsafe = unsafe.toString();
        }

        const unsafe_var = unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
        return this._sanitizer.sanitize(SecurityContext.HTML, unsafe_var);

        // ! Must implement the rest
        // public get sanitizedHtmlProperty() : SafeHtml {
        //     return this._sanitizer.sanitize(SecurityContext.HTML, this._originalHtmlProperty);
        //  }

        //  public get escapedHtmlProperty() : string {
        //     return this.escapeHtml(this._originalHtmlProperty);
        //  }

        //  public get escapedAndSanitizedHtmlProperty() : string {
        //     return this._sanitizer.sanitize(SecurityContext.HTML, this.escapeHtml(this._originalHtmlProperty));
        //  }

        //  escapeHtml(unsafe) {
        //    return unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        //                 .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
        //  }
    }

    uuidV4() {
        const u = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';

        return u.replace(/[xy]/g, function(c) {
            const r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
            return v.toString(16);
        });
    }


    // convert time
    timeAgo(time) {
        const d = new Date();
        const n = d.getTime();
        time = (n - time) / 1000;
        let result;
        if (time < 1) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.second'));
        } else if (time < 60) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', parseInt(time, 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.seconds'));
        } else if (time < 120) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.minute'));
        } else if (time < 3600) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', parseInt((time / 60).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.minutes'));
        } else if (time < 7200) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.hour'));
        } else if (time < 3600 * 24) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', parseInt((time / 3600).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.hours'));
        } else if (time < 3600 * 24 * 2) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.day'));
        } else if (time < 3600 * 24 * 365) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', parseInt((time / (3600 * 24)).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.days'));
        } else if (time < 3600 * 24 * 365 * 2) {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.year'));
        } else {
            result = this.translate.instant('protection.time.issues').toString().replace('{{number}}', parseInt((time / (3600 * 24 * 365)).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.years'));
        }
        return result;
    }

    convertTime2(time) {
        let result = "";

        if (time < 1) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.second'));
        }
        else if (time < 60) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', parseInt(time, 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.seconds'));
        }
        else if (time < 120) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.minute'));
        }
        else if (time < 3600) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', parseInt((time / 60).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.minutes'));
        }
        else if (time < 7200) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.hour'));
        }
        else if (time < 3601 * 24) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', parseInt((time / 3600).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.hours'));
        }
        else if (time < 3600 * 24 * 2) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.day'));
        }
        else if (time < 3600 * 24 * 365) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', parseInt((time / (3600 * 24)).toString(), 10)).replace('{{timeSegment}}', this.translate.instant('protection.time.days'));
        }
        else if (time < 3600 * 24 * 365 * 2) {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', 1).replace('{{timeSegment}}', this.translate.instant('protection.time.year'));
        }
        else {
            return this.translate.instant('protection.time.issues2').toString().replace('{{number}}', parseInt((time / (3600 * 24 * 365)).toString(),10)).replace('{{timeSegment}}', this.translate.instant('protection.time.years'));
        }
    }

    /**
     * This function is used to decode unicode chars that come from connect like this: &#12517;&#12508;&#12540;&#12489; and we want them like this ュボード
     * If string is empty, it returns the empty string
     */
    decodeHTMLEntities(str) {
        if (!str) {
            return str;
        }

        return str.replace(/&#(\d+);/g, function(match, dec) {
            return String.fromCodePoint(dec);
        });
    }

    /**
     * Return the query parameters bit of an url, wihtout the '?'
     * @param url An url like this: /dashboard?param1=<code>
     * @returns A string "param1=<code>"
     */
    computeQueryParams(url) {
        const urlArray = url.split("?");
        const urlSearch = urlArray.length > 1 ? urlArray[1] : "";
        return this.queryStringToJson(urlSearch);
    }

     /**
     * Helper funtion that gets only the path from an url, no serch params
     * It excludes modalContainer from paths because sometimes the url is incorrect and modalContaine sticks to it
     * @param url An url ex.: /dashboard?ceva=ceva
     * @returns Only the path, no search: ex.:/dashboard
     */
     computePath(url) {
        const urlArray = url.split("?");
        let path =  urlArray.length > 0 ? urlArray[0] : "";
        path = this.deleteModalContainer(path);
        return path;
    }

    removeModalContainerFromNavigationURL(path) {
        const indexOfModalContainerInside = path.indexOf("/(modalContainer:");
        const indexOfModalContainer = path.indexOf("(modalContainer:");
        const indexModalContainerCorrect = indexOfModalContainerInside > -1 ? indexOfModalContainerInside : indexOfModalContainer;
        if (indexModalContainerCorrect > -1) {
            path = path.substring(0, indexModalContainerCorrect);
        }
        return path;
    }

     /**
     * Check if it is an alpha instance
     * @returns boolean
     */
     isAlphaInstance() {
        return window.location.hostname === 'alpha-central.bitdefender.com';
    }

    /**
     * Check if it is an alpha or beta instance
     * @returns logo and it's ux needed informations
    */

    getLogo(newDefaultLogo? : any) {

        let logo = newDefaultLogo?.path ? newDefaultLogo : this.valuesService.centralLogos.manufacturer;

        if (this.isAlphaInstance() && this.valuesService.centralLogos.alpha.path) {
            logo = this.valuesService.centralLogos.alpha;
        } else if (this.configService.deployTypeIsBeta() && this.valuesService.centralLogos.beta.path) {
            logo = this.valuesService.centralLogos.beta;
        }

        return logo;
    }

    deleteModalContainer(path) {
        const indexOfModalContainerInside = path.indexOf("/(modalContainer:");
        const indexOfModalContainer = path.indexOf("(modalContainer:");
        const indexModalContainerCorrect = indexOfModalContainerInside > -1 ? indexOfModalContainerInside : indexOfModalContainer;
        const indexOfBracket = path.indexOf(")");

        if (indexModalContainerCorrect > -1 && indexOfBracket > -1) {
            path = path.slice(0, indexModalContainerCorrect) + path.slice(indexOfBracket + 1, path.length);
        }
        return path;
    }

    /**
     * Clean up window.location.href to be sent as ref
     * @returns Window.location.href without any query params
     */
    cleanUpLocationHrefForRef() {
        return window.location.href.split('?')[0];
    }

    getPathFromUrl(url) {
        return url.split('?')[0];
    }

    getParamsFromUrl(url) {
        const urlArray = url.split('?');
        return urlArray.length > 1 ? urlArray[1] : "";
    }

    // send event with device name
    setDeviceName(name) {
        this.invokeEvent.next(name);
    }

    // delay for function ?
    addIncrementalDelayToFunction(f, ...args) {
        let wait = false;
        let timer;
        let start = function(f, args) {
            if (wait) {
                clearTimeout(timer);
                timer = setTimeout( () => {
                    f(args);
                    wait = false;
                }, 1500 );
            } else {
                wait = true;
                timer = setTimeout( () => {
                    f(args);
                    wait = false;
                }, 1500 );
            }
        };
        start(f, args);
    }

    // add zero to a number for date
    addZero(i) {
        if (i < 10) {
            i = '0' + i;
        }
        return i;
    }

    // convert timestamp to full date with month, year, hour, min, sec
    formatTime(UNIX_timestamp) {
        const a = new Date(UNIX_timestamp * 1000);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const year = a.getFullYear();
        const month = months[a.getMonth()];
        const date = a.getDate();
        const hour = this.addZero(a.getHours());
        const min = this.addZero(a.getMinutes());
        const sec = this.addZero(a.getSeconds());
        return date + ' ' + month + ' ' + year + ' ' + hour + ':' + min + ':' + sec;
    }

    // compute date from number of days to a new date with year-month-day
    computeDate(nrDays) {
        const d = new Date();
        d.setDate(d.getDate() - nrDays);
        let month: any = d.getMonth() + 1;
        if (month < 10) {
            month = '0' + month.toString();
        }
        let day: any = d.getDate();
        if (day < 10) {
            day = '0' + day.toString();
        }
        const year = d.getFullYear();
        return year + '-' + month + '-' + day;
    }

    // set days of week: monday-tuesday-wednesday-thursday-friday-saturday-sunday
    getDayOfWeek(date) {
        const dayOfWeek = new Date(date).getDay();
        return isNaN(dayOfWeek) ? null : ['activity.sunday', 'activity.monday', 'activity.tuesday', 'activity.wednesday', 'activity.thursday', 'activity.friday', 'activity.saturday'][dayOfWeek];
    }

    // method returns the time zone difference, in minutes, from current locale
    computeOffset() {
        const d = new Date();
        let dataOffset = d.getTimezoneOffset();
        dataOffset = -1 * dataOffset * 60 * 1000;
        return dataOffset;
    }

    // Method used to return a color based on first letter of a given string
    color(string) {
        let result = '';
        let letter = string.charAt(0).toLowerCase();
        switch (letter) {
            case 'a':
            case 'b':
            case 'c':
                result = 'a';
                break;
            case 'd':
            case 'e':
            case 'f':
                result = 'd';
                break;
            case 'g':
            case 'h':
            case 'i':
                result = 'g';
                break;
            case 'j':
            case 'k':
            case 'l':
                result = 'j';
                break;
            case 'm':
            case 'n':
            case 'o':
                result = 'm';
                break;
            case 'p':
            case 'q':
            case 'r':
                result = 'p';
                break;
            case 's':
            case 't':
            case 'u':
                result = 's';
                break;
            case 'v':
            case 'w':
            case 'x':
                result = 'v';
                break;
            case 'y':
            case 'z':
                result = 'y';
                break;
            default:
            result = 'grey';
                break;
        }

        return result;
    }

    // checkNested(object, prot_1, prop_2,...,prop_n)
    // returns true if object.prop_1.prop_2...prop_n
    // returns false otherwise
    checkNested(...obj) {
        for (let i = 1; i < arguments.length; i++) {
            if (!obj[0] || !obj[0].hasOwnProperty(arguments[i])) {
                return false;
            }
            obj[0] = obj[0][arguments[i]];
        }
        return true;
    }

    // getNested(object, value, prot_1, prop_2,...,prop_n)
    // returns object.prop_1.prop_2...prop_n
    // returns value is one prop is not found
    // it should have more than 2 arguments
    getNested(...obj) {
        if (arguments.length < 2) {
            return null;
        }

        if (typeof obj[0] != "object") {
            return null;
        }

        let defaultValue = arguments[1];
        for (let i = 2; i < arguments.length; i++) {
            if (!obj[0] || !obj[0].hasOwnProperty(arguments[i])) {
                return defaultValue;
            }
            obj[0] = obj[0][arguments[i]];
        }
        return obj[0];
    }

    // setNested(object, value, prot_1, prop_2,...,prop_n)
    // returns true and does: object.prop_1.prop_2...prop_n = value
    // if last prop is not found (prop_n), it creates it and assignes value to the newly created field and returns true
    // if a prop, other than prop_n is not found, it returns true
    // it should have more than 2 arguments
    setNested(...obj) {
        if (arguments.length < 2) {
            return false;
        }

        let setValue = arguments[1];
        let found = true;
        let count = 0;
        for (let i = 2; i < arguments.length; i++) {
            if (!obj[0] || !obj[0].hasOwnProperty(arguments[i])) {
                found = false;
                count = i;
                break;
            }
            obj[0] = obj[0][arguments[i]];
        }
        if (found) {
            obj[0] = setValue;
            return true;
        } else {
            if (count == arguments.length - 1) {
                obj[0][arguments[arguments.length - 1]] = setValue;
                return true;
            }
        }
        return false;
    }

    crc32Convert(str) {
        let table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
        let crc = 0;
        let x: any = 0;
        let y: any = 0;

        crc = crc ^ (-1);
        for (let i = 0, iTop = str.length; i < iTop; i++) {
            y = (crc ^ str.charCodeAt(i)) & 0xFF;
            x = "0x" + table.substr(y * 9, 8);
            crc = (crc >>> 8) ^ x;
        }
        return (crc ^ (-1)) >>> 0;
    }

    formatDate(date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');
    }

    convertSpace(space) {
        if (space < 1024){
            return parseFloat(space.toString()).toFixed(2) + ' B';
        }
        if (space < 1024 * 1024){
            return parseFloat((space / 1024).toString()).toFixed(2) + ' KB';
        }
        if (space < 1024 * 1024 * 1024){
            return parseFloat((space / (1024 * 1024)).toString()).toFixed(2) + ' MB';
        }
        else{
            return parseFloat((space / (1024 * 1024 * 1024)).toString()).toFixed(2) + ' GB';
        }
    }

    // returneaza day/month(key)/year
    convertDate(timestamp) {
        const date = new Date(timestamp);
        return {
            day: date.getUTCDate(),
            year: date.getFullYear(),
            month: 'platform.screentime.month.' + (date.getMonth()+1)
        };
    }

    millisToMinutesAndSeconds(time) {
        const minutes = Math.floor(time / 60000);
        const seconds = ((time % 60000) / 1000).toFixed(0);
        if (parseInt(seconds) < 10) {
            return `${minutes}:0${seconds}`;
        } else {
            return `${minutes}:${seconds}`;
        }
    }

    computePathPieces(entirePath, queryString?) {
        let path = "";
        let subPath = "";
        let queryJson = {};

        if (entirePath.split('/') && entirePath.split('/')[1] && entirePath.split('/')[1].split('?') && entirePath.split('/')[1].split('?')[0]) {
            path = entirePath.split('/')[1].split('?')[0];
        }

        if (entirePath.split('/') && entirePath.split('/')[2] && entirePath.split('/')[2].split('?') && entirePath.split('/')[2].split('?')[0]) {
            subPath = entirePath.split('/')[2].split('?')[0];
        }

        if (queryString) {
            queryJson = this.queryStringToJson(queryString.substring(1));
        }

        return {path, subPath, queryJson};
    }

    getOsVersionInfo() {
        const userAgentObject = userAgentParser(navigator.userAgent);
        const version = {
            max: 0,
            min: 0,
            patch: 0
        };

        if (userAgentObject?.os?.version) {
            const versionList = userAgentObject.os.version.split('.');
            if (versionList.length > 0) {
                version.max = parseInt(versionList[0]);
            }

            if (versionList.length > 1) {
                version.min = parseInt(versionList[1]);
            }

            if (versionList.length > 2) {
                version.patch = parseInt(versionList[2]);
            }
        }

        return version;
    }

    getBrowserInfo() {
        const userAgentObject = userAgentParser(navigator.userAgent);
        const browser = {
            max: 0,
            min: 0,
            patch: 0,
            name: ""
        };

        browser.name = userAgentObject.browser.name.toLowerCase();

        if (userAgentObject?.browser?.version) {
            const versionList = userAgentObject.browser.version.split('.');
            if (versionList.length > 0) {
                browser.max = parseInt(versionList[0]);
            }

            if (versionList.length > 1) {
                browser.min = parseInt(versionList[1]);
            }

            if (versionList.length > 2) {
                browser.patch = parseInt(versionList[2]);
            }
        }

        return browser;
    }

    showSafariExtension() {
       return !this.unsupportedPassManagerOS() && !this.unsupportedPassManagerBrowser();
    }

    olderExceptedMacOsVersion() {
        const osVersion = this.getOsVersionInfo();
        return osVersion.max === 10 && osVersion.min === 11 && osVersion.patch === 5;
    }

    unsupportedPassManagerOS() {
        const deviceOS = this.deviceDetectorService.os.toLocaleLowerCase();
        const osVersion = this.getOsVersionInfo();
        if (deviceOS !== OperatingSystems.MAC
            || (!(osVersion.max === 10  && osVersion.min >= 14) && !(osVersion.max >= 11)
                && !this.olderExceptedMacOsVersion())) {
            return true;
        }
        return false;
    }

    unsupportedPassManagerBrowser() {
        const deviceOS = this.deviceDetectorService.os.toLocaleLowerCase();
        const browserInfo = this.getBrowserInfo();
        if (deviceOS === OperatingSystems.MAC
            && (this.olderExceptedMacOsVersion()
                && !(browserInfo.max >= 11 || (browserInfo.max === 10 && browserInfo.min >= 14)))) {
            return true;
        }
        return false;
    }

    getErrorForChildProfileCreation(error) {
        if ((error?.message?.internal_code === 32005
                && error?.message?.internal_data.code === 39120
                && error?.message?.internal_data.message)
            || (error?.error?.code === 32005
                && error?.error?.data?.code === 39120)) {
            return 'platform.profiles.exceeded.error';
        } else {
            return 'platform.request.error';
        }
    }

    /**
    * Checks if the size of a character given as input is of one Utf8 byte or higher
    * @param {object} input String with the length of maxim 1
    * @returns {boolean}
    */

    isCharacterOverOneUtf8Byte(input): boolean {
        if (typeof input !== 'string') {
            throw new TypeError('Input must be a string');
        }

        const characterCode = input.charCodeAt(0);
        const lastCodeForOneByte = 127;

        if (characterCode > lastCodeForOneByte) {
            return true;
        }

        return false;
    }

    /**
     * Validator used on form inputs for min length
     * @param {string} input String value inserted in form input
     * @param {number} schemaLength Minimum length defined in schemas.ts for that input field
     * @returns {object} if validation conditions are not met
     * @returns {null} otherwise
     */
    public customMinLengthValidator(input: string, schemaLength: number): CustomValidatorInterface|null {
        if (!input) {
            return null;
        }

        const validator = { minlength: true } as CustomValidatorInterface;
        const trimmedInput = (input || '')?.trim() ?? '';
        if (trimmedInput.length < schemaLength
            && !this.isCharacterOverOneUtf8Byte(trimmedInput)) {
            return validator;
        }

        return null;
    }

    /**
     * Validator used on form inputs for max length
     * @param {string} input String value inserted in form input
     * @param {number} schemaLength Maximum length defined in schemas.ts for that input field
     * @returns {object} if validation conditions are not met
     * @returns {null} otherwise
     */
    public customMaxLengthValidator(input: string, schemaLength: number): CustomValidatorInterface|null {
        if (!input) {
            return null;
        }

        const validator = { maxlength: true } as CustomValidatorInterface;
        const trimmedInput = (input || '')?.trim() ?? '';
        if (trimmedInput.length > schemaLength) {
            return validator;
        }

        return null;
    }

    /**
     * Validator used on form inputs for pattern testing
     * @param {string} input String value inserted in form input
     * @param {number} schemaPattern The pattern defined in schemas.ts for that input field
     * @returns {object} if validation conditions are not met
     * @returns {null} otherwise
     */
    public patternValidator(input: string, schemaPattern: string|RegExp): CustomValidatorInterface|null {
        if (!input) {
            return null;
        }

        const validator = { pattern: true } as CustomValidatorInterface;
        const trimmedInput = (input || '')?.trim() ?? '';
        const patternRegex = new RegExp(schemaPattern); // Convert schemaPattern to a regular expression
        if (!patternRegex.test(trimmedInput)) { // Use the test method on the patternRegex
            return validator;
        }

        return null;
    }

    /**
     * Gets current supported password manager browser
     * @public
     * @memberof PasswordManagementService
     * @returns {PasswordManagementBrowsers} The current supported browser
     */
    public getCurrentSupportedBrowser(): PasswordManagementBrowsers {
        const browser = this.deviceInfoService.getDeviceBrowser();
        return this.valuesService.passwordManagementBrowsers[browser];
    }

    /**
     * Decodes a base64url encoded string
     * @param {string} input string to be decoded
     * @returns {string} decoded string
     */
    public decodeBase64Url(input: string): string {
        try {
            const base64EncodedString = input.replace(/-/g, '+').replace(/_/g, '/');
            return atob(base64EncodedString);
        } catch {
            return '';
        }
    }

    /**
     * Maskes an email
     * @param {string} email string to be masked
     * @returns {string} masked email
     */
    public maskEmail(email: string): string {
        const [local, domain] = email.split("@");

        if (local.length <= 2) {
            return `${local[0]}****@${domain}`; // Edge case for very short local
        }

        return `${local[0]}****${local.slice(-3)}@${domain}`;
    }
}
